import { ArrowRight, Loader2, RotateCcw, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StepIcon, stepLabel } from '@/features/workflows/stepIcons';
import { cn } from '@/lib/utils';

import {
  MODAL_GUTTER,
  ModalCheckbox,
  ModalPrimaryButton,
  ModalSecondaryButton,
  ModalSubtitle,
  SessionModalShell,
} from './SessionModalShell';

/**
 * "Request resubmission" — the designer's `RequestResubmissionModal`.
 *
 * Faithful to the reference: 540px container, the section header with its description and
 * the "N of M selected" counter on the right, a scrolling list capped at
 * clamp(260px,40vh,380px), 44px minimum rows, failed rows tinted #FFF8F8 with an #FFE0E0
 * border, selected rows going blue (#0048FF on a 0D tint), the per-row outcome pill, the
 * 18px square checkbox, and the footer sentence naming what will be re-requested.
 *
 * TWO differences from the reference, both because the reference is a mock:
 *
 *   • The checks are THIS RUN'S REAL STEPS, in workflow order, with their real outcomes —
 *     the designer's file hard-codes eight. Icons come from our own `StepIcon`, i.e. the
 *     same marks as the workflow sidebar and the sessions list.
 *   • Only steps that FAILED are preselected. The designer preselects `status === Rejected`;
 *     ours reads the session's step statuses, where the equivalent is `Failed`.
 *
 * WHAT IT DOES TODAY: records the verdict with the selected checks named in the note, which
 * is what the current API supports. Reopening those steps and messaging the applicant is
 * Phase 4 — the dialog is deliberately built first so the selection it captures is the same
 * one that work will act on.
 */

export interface ResubmissionStep {
  position: number;
  stepType: string;
  /** A session step status: Pending | Passed | Failed | … */
  status: string;
  /** The run's own label for the step when it has one, else derived from the type. */
  displayName?: string;
}

export interface ResubmissionTarget {
  referenceId?: string;
  applicantName: string;
  originalStatusLabel: string;
  originalStatusColor: string;
  steps: ResubmissionStep[];
}

/** A step the applicant genuinely has to redo — the ones preselected. */
function isFailed(status: string): boolean {
  return status === 'Failed';
}

export function RequestResubmissionModal({
  open,
  target,
  isSaving,
  errorMessage,
  onClose,
  onSubmit,
}: {
  open: boolean;
  target: ResubmissionTarget | null;
  isSaving: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (note: string, positions: number[]) => void;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const steps = useMemo(
    () => (target ? [...target.steps].sort((a, b) => a.position - b.position) : []),
    [target],
  );

  // Preselect the failed steps each time the dialog opens, not on every render — otherwise
  // unticking a row would be undone immediately. Keyed on the run so reopening resets.
  const initialisedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !target) {
      initialisedFor.current = null;
      return;
    }
    const key = `${target.referenceId ?? ''}:${steps.length}`;
    if (initialisedFor.current === key) return;
    initialisedFor.current = key;
    setSelected(new Set(steps.filter((s) => isFailed(s.status)).map((s) => s.position)));
  }, [open, target, steps]);

  if (!open || !target) return null;

  const names = steps
    .filter((s) => selected.has(s.position))
    .map((s) => (s.displayName || stepLabel(s.stepType)).toLowerCase());

  function toggle(position: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(position)) next.delete(position);
      else next.add(position);
      return next;
    });
  }

  return (
    <SessionModalShell
      open={open}
      onClose={onClose}
      // 620px, matching the decision dialogs so the family reads as one set. The designer's
      // 540 left the step name, its outcome pill and the checkbox competing for the same
      // row, and a long step name pushed the row wider than the dialog.
      maxWidth="620px"
      icon={<RotateCcw size={16} className="text-[#0048FF]" />}
      title="Request resubmission"
      subtitle={
        <ModalSubtitle
          referenceId={target.referenceId}
          applicantName={target.applicantName}
          status={target.originalStatusLabel}
          statusColor={target.originalStatusColor}
        />
      }
      footer={
        <>
          <ModalSecondaryButton onClick={onClose} disabled={isSaving}>
            <X size={15} />
            Cancel
          </ModalSecondaryButton>
          <ModalPrimaryButton
            onClick={() => {
              const positions = [...selected].sort((a, b) => a - b);
              onSubmit(`Resubmission requested for: ${names.join(', ')}`, positions);
            }}
            disabled={isSaving || selected.size === 0}
            title={selected.size === 0 ? 'Select at least one check' : undefined}
          >
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            {isSaving ? 'Saving…' : 'Send resubmission request'}
          </ModalPrimaryButton>
        </>
      }
    >
      {/* min-w-0 all the way down. Each of these is a flex item whose default minimum is
          its content width, and any one of them refusing to shrink was enough to make the
          body a pixel too wide and hand it a horizontal scrollbar. */}
      <div className={cn('flex min-w-0 flex-col py-[clamp(14px,1.2vw,18px)]', MODAL_GUTTER)}>
        <div className="mb-[clamp(8px,0.8vw,12px)] flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="m-0 text-[clamp(14px,1.1vw,16px)] font-semibold leading-tight text-[#041A3A]">
              Workflow checks
            </h3>
            <p className="m-0 mt-0.5 text-[clamp(11px,0.85vw,12.5px)] font-normal leading-normal text-[#888888]">
              Failed checks are pre-selected for resubmission. You can adjust the selection before
              sending.
            </p>
          </div>
          <span className="mt-0.5 shrink-0 whitespace-nowrap text-[clamp(11px,0.85vw,12.5px)] font-normal text-[#888888]">
            {selected.size} of {steps.length} selected
          </span>
        </div>

        <div className="flex min-w-0 max-h-[clamp(260px,40vh,380px)] flex-col gap-[clamp(4px,0.4vw,6px)] overflow-y-auto overflow-x-hidden pr-0.5">
          {steps.map((step) => {
            const isChecked = selected.has(step.position);
            const failed = isFailed(step.status);
            return (
              <label
                key={step.position}
                className={cn(
                  // `relative` so the visually-hidden checkbox is positioned against its own
                  // row. Absolutely positioned against a distant ancestor, focusing it made
                  // the browser scroll the whole list to "reveal" it.
                  'relative flex min-h-[44px] min-w-0 cursor-pointer items-center justify-between gap-3 rounded-lg border-[1.5px] px-[clamp(12px,1.2vw,16px)] py-[clamp(10px,1vw,13px)] transition-colors duration-150',
                  // Selection wins over the failed tint, exactly as the designer's
                  // `--selected.--rejected` rule does: the blue says "this one is going
                  // back", which is more important than "this one failed".
                  isChecked
                    ? 'border-[#0048FF] bg-[#0048FF0D]'
                    : failed
                      ? 'border-[#FFE0E0] bg-[#FFF8F8] hover:bg-[#FFF2F2]'
                      : 'border-[#ECEEF3] bg-white hover:border-[#D0D4E4] hover:bg-[#FAFBFD]',
                )}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <StepIcon stepType={step.stepType} tone={failed ? 'red' : 'neutral'} />
                  <span className="truncate text-[clamp(12px,1vw,14px)] font-medium leading-snug text-[#041A3A]">
                    {step.displayName || stepLabel(step.stepType)}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-[clamp(10px,1vw,14px)]">
                  <StepOutcomePill status={step.status} />
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(step.position)}
                    className="sr-only"
                  />
                  <ModalCheckbox checked={isChecked} />
                </div>
              </label>
            );
          })}
        </div>

        <p className="m-0 mt-[clamp(14px,1.2vw,18px)] text-[clamp(11px,0.85vw,12.5px)] font-normal leading-normal text-[#666666]">
          {selected.size > 0 ? (
            <>
              <strong className="font-semibold text-[#041A3A]">
                {selected.size} check{selected.size === 1 ? '' : 's'}
              </strong>{' '}
              will be re-requested: {names.join(', ')}.
            </>
          ) : (
            <span>Select at least one check to request resubmission.</span>
          )}
        </p>

        {errorMessage && (
          <p className="m-0 mt-2 text-[clamp(11px,0.85vw,12.5px)] leading-snug text-[#BF3636]">
            {errorMessage}
          </p>
        )}
      </div>
    </SessionModalShell>
  );
}

/**
 * The designer's per-row outcome pill: 4px/8px, 11px icon, colour by outcome.
 *
 * Their mock has only Approved and Rejected, because a mock has no Pending steps. A real
 * run does, so the untouched steps get the neutral grey the rest of the app already uses
 * for "nothing has happened yet" rather than being mislabelled as passed.
 */
function StepOutcomePill({ status }: { status: string }) {
  const meta =
    status === 'Passed'
      ? { label: 'Approved', color: '#00C232' }
      : status === 'Failed'
        ? { label: 'Rejected', color: '#BF3636' }
        : { label: 'Not done', color: '#B3B3B3' };

  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-1 text-[clamp(10px,0.8vw,11.5px)] font-medium leading-none"
      style={{ color: meta.color, borderColor: meta.color, backgroundColor: `${meta.color}0D` }}
    >
      {meta.label}
    </span>
  );
}
